/**
 * secureStorage — abstraction over @capacitor/preferences (native) +
 * localStorage (web/dev fallback).
 *
 * ⚠️ REALIDAD DEL STORAGE (C18 — corrige el header anterior, que era FALSO):
 *   - Native (iOS): @capacitor/preferences escribe en **UserDefaults**
 *     (un .plist en texto plano dentro del sandbox de la app). NO usa
 *     Keychain ni `kSecAttrAccessibleAfterFirstUnlock`. El contenido entra
 *     en backups (iTunes/iCloud según configuración del dispositivo).
 *   - Native (Android): @capacitor/preferences escribe en **SharedPreferences**
 *     (XML en texto plano en el data dir de la app). NO usa
 *     EncryptedSharedPreferences ni androidx.security.
 *   - Web (dev/PWA build): localStorage, texto plano (superficie conocida).
 *
 * RIESGO REAL: el refreshToken (vida 7 días) queda persistido EN CLARO en
 * el plist/XML del dispositivo. La única protección efectiva es el sandbox
 * del OS — un dispositivo con jailbreak/root, un backup extraído o
 * tooling forense puede leerlo directamente.
 *
 * MITIGACIÓN PENDIENTE (ciclo futuro, requiere device testing + re-login
 * de usuarios mobile): migrar a un plugin real de Keychain (iOS) /
 * EncryptedSharedPreferences-Keystore (Android). Ver
 * `docs/PENDING-HUMANS.md` → entrada "secureStorage → Keychain/Keystore".
 *
 * El nombre `secureStorage` se conserva por compatibilidad con los callers
 * (auth bootstrap); léase como "storage de secretos" y NO como una garantía
 * de cifrado at-rest. Este archivo NO cambia comportamiento en C18: solo
 * documenta la realidad (PR-2 `cycle18/smoke-securestorage`).
 *
 * Used by the auth bootstrap to persist refreshToken outside of the Zustand
 * `persist` middleware so the token never lands in localStorage on native
 * builds (localStorage es legible por cualquier JS inyectado — XSS; el
 * storage nativo al menos exige acceso al filesystem del dispositivo).
 *
 * All methods are async and return Promises so the API is identical for both
 * backends; web callers do not pay any real cost (localStorage is sync but
 * we wrap the result in Promise.resolve).
 */
import { IS_MOBILE } from './platform';

export interface SecureStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

// Lazy import so jsdom tests don't need to load @capacitor/preferences (which
// requires a real native bridge and throws in pure-Node contexts).
async function getPreferences() {
  const mod = await import('@capacitor/preferences');
  return mod.Preferences;
}

const nativeStorage: SecureStorage = {
  async getItem(key) {
    const Preferences = await getPreferences();
    const result = await Preferences.get({ key });
    return result.value ?? null;
  },
  async setItem(key, value) {
    const Preferences = await getPreferences();
    await Preferences.set({ key, value });
  },
  async removeItem(key) {
    const Preferences = await getPreferences();
    await Preferences.remove({ key });
  },
  async clear() {
    const Preferences = await getPreferences();
    await Preferences.clear();
  },
};

const webStorage: SecureStorage = {
  async getItem(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      // Storage may be unavailable (private mode, quota exceeded, SSR).
      return null;
    }
  },
  async setItem(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Swallow — quota or unavailable.
    }
  },
  async removeItem(key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Swallow.
    }
  },
  async clear() {
    try {
      window.localStorage.clear();
    } catch {
      // Swallow.
    }
  },
};

export const secureStorage: SecureStorage = IS_MOBILE ? nativeStorage : webStorage;

// Export both backends so tests / power users can opt in explicitly.
export const _internals = { nativeStorage, webStorage };
